import type React from 'react'
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { Input, InputNumber, InputRef, Tooltip } from 'antd'
import type { ICellEditorParams } from 'ag-grid-community'

type Props = ICellEditorParams & {
  accessor?: string
  data: any
  minValue?: number
  maxValue?: number
  numberType?: 'int' | 'float'
  precision?: number
}

export const NumberCellEditor: React.FC<Props> = forwardRef((props, ref) => {
  const { data, value: propValue, accessor = '', numberType = 'int' } = props
  const [value, setValue] = useState<number | null>(parseNumber(data[accessor] || propValue, numberType))
  const refInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (refInput.current) {
      refInput.current.focus()
    }
  }, [])

  /* Component Editor Lifecycle methods */
  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return value
      },

      // Gets called once before editing starts, to give editor a chance to
      // cancel the editing before it even starts.
      isCancelBeforeStart() {
        return false
      },

      // Gets called once when editing is finished (eg if Enter is pressed).
      // If you return true, then the result of the edit will be ignored.
      isCancelAfterEnd() {
        return false
      },
    }
  })

  return (
    <Tooltip title='Enter to save' placement='top' mouseEnterDelay={0.5}>
      <InputNumber
        ref={refInput}
        value={value}
        onChange={(v) => setValue(v)}
        style={{ width: '100%', border: 'none' }}
        min={props.minValue}
        max={props.maxValue}
        precision={props.precision}
      />
    </Tooltip>
  )
})

function parseNumber(value: string, numberType: 'int' | 'float'): number {
  if (numberType === 'int') {
    return parseInt(value)
  }
  return parseFloat(value)
}
